import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { plyxApi } from "@/lib/plyxApi";
import { usePlyxAuth } from "@/context/PlyxAuthContext";
import { Loader2, ArrowLeft, Sparkles, Globe, Shield, Music, Smile, Flame, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  { code: "talent",  label: "Talent Show",   Icon: Sparkles, desc: "Voice / BGT format. Open audition, viewers vote." },
  { code: "country", label: "Country Battle", Icon: Globe,   desc: "🇳🇬 vs 🇬🇭. Pick countries, rally your flag." },
  { code: "club",    label: "Club Derby",    Icon: Shield,    desc: "Arsenal vs Chelsea. Fans throw hands (peacefully)." },
  { code: "dance",   label: "Dance Battle",  Icon: Music,     desc: "Pick the move. Best execution wins." },
  { code: "comedy",  label: "Comedy Roast",  Icon: Smile,     desc: "Funniest entry takes the pot." },
  { code: "general", label: "Other",         Icon: Flame,     desc: "Anything else — fishing, cooking, fashion runway, you name it." },
];

const DURATION_PRESETS = [
  { hours: 24,  label: "24 hours" },
  { hours: 72,  label: "3 days" },
  { hours: 168, label: "1 week" },
  { hours: 336, label: "2 weeks" },
];

const PlyxChallengeNew = () => {
  const { user, loading: authLoading } = usePlyxAuth();
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);
  const [template, setTemplate] = useState("talent");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [duration, setDuration] = useState(72);
  const [prize, setPrize] = useState(0);
  const [hearts, setHearts] = useState(true);
  const [gifts, setGifts] = useState(true);
  const [maxEntries, setMaxEntries] = useState(0);
  const [countryTags, setCountryTags] = useState("");
  const [clubTags, setClubTags] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!user) return;
    plyxApi.get("/plyx/challenges/eligibility")
      .then((r) => setElig(r.data))
      .catch(() => setElig({ can_create: false, reason: "Could not check eligibility" }));
  }, [user]);

  if (!authLoading && !user) return <Navigate to="/plyx/login?next=/plyx/challenges/new" replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || title.trim().length < 3) {
      toast.error("Title must be at least 3 characters");
      return;
    }
    setCreating(true);
    try {
      const { data } = await plyxApi.post("/plyx/challenges", {
        template,
        title: title.trim(),
        description: description.trim(),
        duration_hours: Number(duration),
        prize_pool_coins: Number(prize) || 0,
        hearts_voting: hearts,
        gifts_voting: gifts,
        max_entries: Number(maxEntries) || 0,
        country_tags: countryTags.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 12),
        club_tags: clubTags.split(",").map((s) => s.trim()).filter(Boolean).slice(0, 12),
      });
      toast.success("Challenge live! Share the link.");
      navigate(`/plyx/challenges/${data.challenge_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not create challenge");
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="plyx-challenge-new-page">
      <div className="max-w-2xl mx-auto px-5 py-8 pb-24">
        <Link to="/plyx/challenges" className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white mb-4">
          <ArrowLeft className="w-4 h-4"/> back to challenges
        </Link>
        <h1 className="font-display text-4xl tracking-tight mb-2">Create a challenge</h1>
        <p className="text-sm text-white/60 mb-6">
          Pick a format, set a prize pool, share the link. Top 3 entries split 50/30/20.
        </p>

        {/* Eligibility banner */}
        {elig && !elig.can_create && (
          <div className="mb-6 rounded-2xl border border-red-500/40 bg-red-500/10 p-4 flex items-start gap-3" data-testid="plyx-challenge-elig-blocked">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5"/>
            <div className="text-sm">
              <div className="font-semibold text-red-300 mb-1">Not eligible yet</div>
              <div className="text-white/70">{elig.reason}</div>
              {elig.requirements_met && (
                <div className="text-[11px] text-white/50 mt-2">
                  You have: {elig.requirements_met.follower_count} followers · {elig.requirements_met.account_age_days} days old · {elig.requirements_met.verified ? "verified ✓" : "not verified"}
                </div>
              )}
            </div>
          </div>
        )}
        {elig?.can_create && (
          <div className="mb-6 rounded-2xl border border-[#00E0C7]/30 bg-[#00E0C7]/5 p-3 text-xs text-[#00E0C7]" data-testid="plyx-challenge-elig-ok">
            ✓ Eligible via <strong>{elig.reason}</strong>. You can create up to 2 active challenges at once.
          </div>
        )}

        <form onSubmit={submit} className="space-y-5" data-testid="plyx-challenge-new-form">
          {/* Template grid */}
          <div>
            <label className="text-xs uppercase tracking-wider text-white/50 mb-2 block">Format</label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {TEMPLATES.map((t) => {
                const { Icon } = t;
                const active = template === t.code;
                return (
                  <button
                    type="button"
                    key={t.code}
                    onClick={() => setTemplate(t.code)}
                    data-testid={`plyx-challenge-new-template-${t.code}`}
                    className={`p-3 rounded-xl border text-left transition-all ${
                      active
                        ? "border-[#FFB42D] bg-[#FFB42D]/10"
                        : "border-white/10 bg-white/5 hover:border-white/30"
                    }`}
                  >
                    <Icon className="w-5 h-5 mb-1.5" style={{ color: active ? "#FFB42D" : "rgba(255,255,255,0.7)" }}/>
                    <div className="font-semibold text-sm">{t.label}</div>
                    <div className="text-[10px] text-white/50 mt-0.5 line-clamp-2">{t.desc}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <Field label="Title" required>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="The 2026 Lagos Talent Showdown"
              maxLength={120}
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
              data-testid="plyx-challenge-new-title"
            />
          </Field>

          <Field label="Description">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2400}
              placeholder="Rules, theme, anything entrants should know…"
              className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
              data-testid="plyx-challenge-new-description"
            />
          </Field>

          {(template === "country" || template === "general") && (
            <Field label="Country tags (comma-separated ISO codes)">
              <input
                value={countryTags}
                onChange={(e) => setCountryTags(e.target.value)}
                placeholder="NG, GH, KE"
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm uppercase"
                data-testid="plyx-challenge-new-countries"
              />
            </Field>
          )}
          {(template === "club" || template === "general") && (
            <Field label="Club tags (comma-separated)">
              <input
                value={clubTags}
                onChange={(e) => setClubTags(e.target.value)}
                placeholder="arsenal, chelsea, man-city"
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
                data-testid="plyx-challenge-new-clubs"
              />
            </Field>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Field label="Duration">
              <select
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm"
                data-testid="plyx-challenge-new-duration"
              >
                {DURATION_PRESETS.map((p) => (
                  <option key={p.hours} value={p.hours}>{p.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Prize pool (coins)">
              <input
                value={prize}
                onChange={(e) => setPrize(e.target.value)}
                type="number"
                min="0"
                step="100"
                placeholder="0"
                className="w-full px-3 py-2 rounded-lg bg-black/40 border border-white/10 focus:border-[#FFB42D] outline-none text-sm tabular-nums"
                data-testid="plyx-challenge-new-prize"
              />
            </Field>
          </div>

          <div className="rounded-xl border border-white/10 bg-white/5 p-3 space-y-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={hearts}
                onChange={(e) => setHearts(e.target.checked)}
                className="accent-[#FFB42D]"
                data-testid="plyx-challenge-new-hearts"
              />
              <span className="text-sm">Allow heart votes (free)</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={gifts}
                onChange={(e) => setGifts(e.target.checked)}
                className="accent-[#FFB42D]"
                data-testid="plyx-challenge-new-gifts"
              />
              <span className="text-sm">Allow gift votes (coins) — gifts to entries count toward the leaderboard</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={creating || (elig && !elig.can_create)}
            data-testid="plyx-challenge-new-submit"
            className="w-full px-5 py-3 rounded-full font-semibold text-[#0A0A0A] text-sm disabled:opacity-50 inline-flex items-center justify-center gap-2"
            style={{ background: "linear-gradient(135deg, #FF2D7A 0%, #FFB42D 50%, #00E0C7 100%)" }}
          >
            {creating ? <Loader2 className="w-4 h-4 animate-spin"/> : null}
            Launch challenge
          </button>
        </form>
      </div>
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <div>
    <label className="text-xs uppercase tracking-wider text-white/50 mb-1.5 block">
      {label}{required && <span className="text-red-400"> *</span>}
    </label>
    {children}
  </div>
);

export default PlyxChallengeNew;
